import { Alert, Lock } from '@airtm/airtm-ui';
import { createContext, useContext, useMemo } from 'react';
import type { ComponentProps, ComponentType, ReactNode } from 'react';
import useMe from 'utils/hooks/useMe/useMe';
import { useHistory, useLocation } from 'react-router-dom';
import { accountStatus as ACCOUNT_STATUS } from '@airtm/utils/dist/constants';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import isUserVerified from '@airtm/utils/dist/utilities/isUserVerified';

type InvalidPayoutStatusState = {
  disablePayouts?: boolean;
  alerts?: ReactNode[];
};

export const InvalidPayoutStatusContext = createContext<InvalidPayoutStatusState>({
  disablePayouts: false,
});

export const useInvalidPayoutStatus = (): InvalidPayoutStatusState =>
  useContext(InvalidPayoutStatusContext);

const MfaAlert = function ({ t, onAction }: { t: TFunction; onAction: () => void }) {
  return (
    <Alert
      icon={<Lock />}
      title={t('ENTERPRISE_ALERTS:ENTERPRISE_2FA_ALERT_TITLE')}
      variant="warning"
      btnAction={onAction}
      btnLabel={t('ENTERPRISE_ALERTS:ENTERPRISE_2FA_ALERT_CTA')}
      className="u-mb-1"
      key="mfa-alert"
    />
  );
};

const DocumentationAlert = function ({ t }: { t: TFunction }) {
  return (
    <Alert
      title={
        <Trans
          components={[
            <a
              href={t('ENTERPRISE_ALERTS:CORPORATE_DOCUMENTATION_FORM_LINK')}
              className="text--sm text--currentcolor"
              target="_blank"
              rel="noreferrer noopener"
              key="submit-documentation-link"
            >
              submit your business documentation
            </a>,
          ]}
          i18nKey="ENTERPRISE_ALERTS:ENTERPRISE_CORPORATE_DOCUMENTATION_ALERT_TITLE"
        />
      }
      variant="warning"
      className="u-mb-1"
      key="docs-alert"
    />
  );
};

const LimitedAlert = function ({ t }: { t: TFunction }) {
  return (
    <Alert
      title={t('ENTERPRISE_ALERTS:ENTERPRISE_LIMITED_ACCOUNT_ALERT_TITLE')}
      variant="warning"
      className="u-mb-1"
      key="limited-alert"
    />
  );
};

const NoFundsAlert = function ({ t, onAction }: { t: TFunction; onAction: () => void }) {
  return (
    <Alert
      title={t('ENTERPRISE_ALERTS:ENTERPRISE_NO_FUNDS_ALERT_TITLE')}
      variant="warning"
      btnAction={onAction}
      btnLabel={t('ENTERPRISE_ALERTS:ENTERPRISE_NO_FUNDS_ALERT_CTA')}
      className="u-mb-1"
      key="funds-alert"
    >
      <p>{t('ENTERPRISE_ALERTS:ENTERPRISE_NO_FUNDS_ALERT_DESCRIPTION')}</p>
    </Alert>
  );
};

export default function withInvalidPayoutStatus(Component: ComponentType) {
  const WithInvalidPayoutStatusWrapper = function (props: ComponentProps<any>) {
    const { me, loading, custodialProvider } = useMe({
      include: { preferences: true, securityHub: true, enterprise: true },
      fetchPolicy: 'cache-and-network',
    });
    const history = useHistory();
    const location = useLocation();

    const { t } = useTranslation(['ENTERPRISE_ALERTS']);
    const hasBalance = Number(me?.enterprise?.balance?.amount) > 0;
    const isPayoutPath = location.pathname.startsWith('/payout');

    const context: InvalidPayoutStatusState = useMemo(() => {
      const isMfaEnabled = me?.preferences?.mfa?.method && me?.preferences?.mfa?.operations;
      const alerts = [];
      const goToSecuritySettings = () => history.push('/settings/security');
      const goToAddFunds = () => history.push('/fund');

      if (!loading) {
        if (
          me &&
          !isMfaEnabled &&
          isPayoutPath &&
          me?.organizationPermissions?.canApproveBulkPayment
        ) {
          alerts.push(<MfaAlert t={t} onAction={goToSecuritySettings} />);
        }
        if (!isUserVerified(me?.securityHub)) {
          alerts.push(<DocumentationAlert t={t} />);
        } else if (me?.status === ACCOUNT_STATUS.LIMITED) {
          alerts.push(<LimitedAlert t={t} />);
        }
        if (custodialProvider && !hasBalance && me?.organizationPermissions?.canAddFunds) {
          alerts.push(<NoFundsAlert t={t} onAction={goToAddFunds} />);
        }
      }

      return {
        disablePayouts: alerts.length > 0,
        alerts,
      };
    }, [me, loading, t, history, hasBalance, custodialProvider, isPayoutPath]);

    return (
      <InvalidPayoutStatusContext.Provider value={context}>
        {}
        <Component {...props} />
      </InvalidPayoutStatusContext.Provider>
    );
  };
  return WithInvalidPayoutStatusWrapper;
}
